/**
 * IntegrityPage.js - Verificação de Integridade do Sistema
 * CG Business ERP - Anti-Fraude e Auditoria
 * 
 * Features:
 * - Verificação de integridade de dados
 * - Detecção de actividades suspeitas
 * - Histórico de verificações
 * - Resumo de auditoria
 */

import { useState, useEffect } from 'react';
import { integrityAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Shield, ShieldCheck, ShieldAlert, ShieldX,
  RefreshCw, AlertTriangle, CheckCircle, XCircle,
  History, Eye, Activity, FileText, Users, Package,
  TrendingUp, Clock, Search, Info, Download, FileSpreadsheet,
  FileDown, ChevronRight, ExternalLink
} from 'lucide-react';
import { formatDisplayCode } from '@/lib/utils';
import { cn, formatDateTime, formatCurrency } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

// Corporate Colors
const COLORS = {
  orange: '#F6931F',
  blue: '#2B4DAA'
};

// Issue severity colors
const SEVERITY_CONFIG = {
  critical: { color: 'red', icon: ShieldX, label: 'Crítico', bg: 'bg-red-100' },
  warning: { color: 'amber', icon: AlertTriangle, label: 'Aviso', bg: 'bg-amber-100' },
  info: { color: 'blue', icon: Info, label: 'Info', bg: 'bg-blue-100' }
};

// Category icons
const CATEGORY_ICONS = {
  sales: ShieldCheck,
  stock: Package,
  cash: TrendingUp,
  users: Users,
  receivables: FileText
};

export default function IntegrityPage() {
  const { user, hasRole } = useAuth();
  const canAccess = hasRole(['admin', 'store_manager']);
  
  const [loading, setLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [suspicious, setSuspicious] = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('check');
  
  // Detail modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [exportingAuditSummary, setExportingAuditSummary] = useState(false);
  
  // Map issue messages to issue types for API
  const issueTypeMap = {
    "vendas sem funcionário atribuído": "sales_no_employee",
    "vendas confirmadas sem itens": "sales_no_items",
    "vendas com valor negativo": "sales_negative",
    "vendas com desconto superior a 50%": "sales_high_discount",
    "produtos com stock negativo": "stock_negative",
    "produtos sem categoria": "stock_no_category",
    "items vendidos ainda marcados como 'em stock'": "items_wrong_state"
  };
  
  const getIssueType = (message) => {
    const lowerMsg = message.toLowerCase();
    for (const [key, value] of Object.entries(issueTypeMap)) {
      if (lowerMsg.includes(key)) return value;
    }
    return null;
  };
  
  useEffect(() => {
    if (canAccess) {
      loadHistory();
      loadAuditSummary();
    }
  }, [canAccess]);
  
  const loadHistory = async () => {
    try {
      const res = await integrityAPI.getHistory(10);
      setHistory(res.data || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };
  
  const loadAuditSummary = async () => {
    try {
      const res = await integrityAPI.getAuditSummary(30);
      setAuditSummary(res.data);
    } catch (error) {
      console.error('Error loading audit summary:', error);
    }
  };
  
  const runIntegrityCheck = async () => {
    setLoading(true);
    try {
      const res = await integrityAPI.runCheck();
      setCheckResult(res.data);
      await loadHistory();
      
      if (res.data.status === 'passed') {
        toast.success('Verificação concluída - Sistema íntegro!');
      } else {
        toast.warning('Verificação encontrou problemas críticos');
      }
    } catch (error) {
      toast.error('Erro ao executar verificação');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const checkSuspiciousActivities = async () => {
    setLoading(true);
    try {
      const res = await integrityAPI.getSuspiciousActivities(30);
      setSuspicious(res.data);
      
      if (res.data.total_findings === 0) {
        toast.success('Nenhuma actividade suspeita detectada!');
      } else {
        toast.warning(`${res.data.total_findings} actividades suspeitas encontradas`);
      }
    } catch (error) {
      toast.error('Erro ao verificar actividades suspeitas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const viewIssueDetails = async (issue) => {
    const issueType = getIssueType(issue.message);
    if (!issueType) {
      toast.error('Tipo de problema não suportado para visualização');
      return;
    }
    
    setSelectedIssue({ ...issue, issueType });
    setDetailModalOpen(true);
    setDetailLoading(true);
    
    try {
      const res = await integrityAPI.getIssueDetails(issueType);
      setDetailData(res.data);
    } catch (error) {
      console.error('Error loading issue details:', error);
      toast.error('Erro ao carregar detalhes');
    } finally {
      setDetailLoading(false);
    }
  };
  
  const exportReport = async (format) => {
    if (!selectedIssue?.issueType) return;
    
    try {
      toast.info(`A gerar ${format.toUpperCase()}...`);
      const response = await integrityAPI.exportIssue(selectedIssue.issueType, format);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : format === 'pdf' 
          ? 'application/pdf'
          : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `integridade_${selectedIssue.issueType}_${new Date().toISOString().slice(0,10)}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success(`${format.toUpperCase()} exportado com sucesso!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar relatório');
    }
  };

  const exportAuditSummaryPdf = async () => {
    try {
      setExportingAuditSummary(true);
      const response = await integrityAPI.exportAuditSummaryPdf(30);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria_sistema_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('PDF premium de auditoria exportado com sucesso!');
    } catch (error) {
      console.error('Audit summary export error:', error);
      toast.error('Erro ao exportar PDF premium de auditoria');
    } finally {
      setExportingAuditSummary(false);
    }
  };
  
  if (!canAccess) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <ShieldX className="h-16 w-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Acesso Restrito</h2>
          <p className="text-slate-500">
            Apenas Administradores e Gerentes podem aceder a esta página.
          </p>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-6" data-testid="integrity-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div 
            className="p-3 rounded-xl shadow-lg"
            style={{ background: `linear-gradient(135deg, ${COLORS.blue} 0%, #1E3A8A 100%)` }}
          >
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Verificação de Integridade
            </h1>
            <p className="text-sm text-slate-500">
              Anti-fraude e auditoria do sistema
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={runIntegrityCheck}
            disabled={loading}
            style={{ background: `linear-gradient(135deg, ${COLORS.orange} 0%, #E8850F 100%)` }}
            className="text-white shadow-lg"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4 mr-2" />
            )}
            Verificar Sistema
          </Button>
        </div>
      </div>
      
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Última Verificação</p>
                <p className="text-lg font-bold text-slate-900">
                  {history[0]?.timestamp 
                    ? new Date(history[0].timestamp).toLocaleDateString('pt-PT', { timeZone: 'Africa/Maputo' })
                    : 'Nunca'}
                </p>
              </div>
              <Clock className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Estado do Sistema</p>
                <p className={cn(
                  "text-lg font-bold",
                  (checkResult?.status === 'passed' || history[0]?.status === 'passed')
                    ? "text-emerald-600"
                    : checkResult?.status === 'failed' 
                    ? "text-red-600"
                    : "text-slate-900"
                )}>
                  {checkResult?.status === 'passed' || history[0]?.status === 'passed'
                    ? 'Íntegro'
                    : checkResult?.status === 'failed'
                    ? 'Com Problemas'
                    : 'Não Verificado'}
                </p>
              </div>
              {(checkResult?.status === 'passed' || history[0]?.status === 'passed') ? (
                <CheckCircle className="h-8 w-8 text-emerald-500" />
              ) : checkResult?.status === 'failed' ? (
                <XCircle className="h-8 w-8 text-red-500" />
              ) : (
                <Shield className="h-8 w-8 text-slate-400" />
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Acções Auditadas</p>
                <p className="text-lg font-bold text-slate-900">
                  {auditSummary?.total_audited_actions || 0}
                </p>
                <p className="text-xs text-slate-400">últimos 30 dias</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Act. Suspeitas</p>
                <p className={cn(
                  "text-lg font-bold",
                  suspicious?.total_findings > 0 ? "text-amber-600" : "text-emerald-600"
                )}>
                  {suspicious?.total_findings ?? '-'}
                </p>
              </div>
              {suspicious?.total_findings > 0 ? (
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              ) : (
                <ShieldCheck className="h-8 w-8 text-emerald-500" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="check" className="gap-2">
            <ShieldCheck className="h-4 w-4" />
            Verificação
          </TabsTrigger>
          <TabsTrigger value="suspicious" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Act. Suspeitas
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Histórico
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2">
            <FileText className="h-4 w-4" />
            Auditoria
          </TabsTrigger>
        </TabsList>
        
        {/* Check Tab */}
        <TabsContent value="check">
          {checkResult ? (
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {checkResult.status === 'passed' ? (
                      <div className="p-2 rounded-full bg-emerald-100">
                        <CheckCircle className="h-6 w-6 text-emerald-600" />
                      </div>
                    ) : (
                      <div className="p-2 rounded-full bg-red-100">
                        <XCircle className="h-6 w-6 text-red-600" />
                      </div>
                    )}
                    <div>
                      <CardTitle>
                        {checkResult.status === 'passed' 
                          ? 'Sistema Íntegro' 
                          : 'Problemas Detectados'}
                      </CardTitle>
                      <CardDescription>
                        Verificado por {checkResult.checked_by} em {new Date(checkResult.timestamp).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' })}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={checkResult.summary.critical > 0 ? 'destructive' : 'secondary'}>
                      {checkResult.summary.critical} Críticos
                    </Badge>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {checkResult.summary.warnings} Avisos
                    </Badge>
                    <Badge variant="outline">
                      {checkResult.summary.info} Info
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {checkResult.issues.length === 0 ? (
                  <div className="text-center py-12">
                    <ShieldCheck className="h-16 w-16 mx-auto text-emerald-400 mb-4" />
                    <p className="text-lg font-medium text-slate-900">Nenhum problema encontrado!</p>
                    <p className="text-sm text-slate-500">O sistema está a funcionar correctamente.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {checkResult.issues.map((issue, idx) => {
                      const config = SEVERITY_CONFIG[issue.type];
                      const Icon = config.icon;
                      const CategoryIcon = CATEGORY_ICONS[issue.category] || Shield;
                      
                      return (
                        <div 
                          key={idx}
                          className={cn(
                            "flex items-start gap-4 p-4 rounded-lg border",
                            config.bg
                          )}
                        >
                          <div className={cn(
                            "p-2 rounded-full",
                            `bg-${config.color}-200`
                          )}>
                            <Icon className={cn("h-5 w-5", `text-${config.color}-600`)} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                <CategoryIcon className="h-3 w-3 mr-1" />
                                {issue.category}
                              </Badge>
                              <Badge className={cn(
                                "text-xs",
                                issue.type === 'critical' && "bg-red-500",
                                issue.type === 'warning' && "bg-amber-500",
                                issue.type === 'info' && "bg-blue-500"
                              )}>
                                {config.label}
                              </Badge>
                            </div>
                            <p className="font-medium text-slate-900">{issue.message}</p>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">Acção:</span> {issue.action}
                            </p>
                          </div>
                          {getIssueType(issue.message) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewIssueDetails(issue)}
                              className="shrink-0"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver Lista
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/80 backdrop-blur border-slate-200">
              <CardContent className="py-16 text-center">
                <Shield className="h-20 w-20 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Pronto para Verificação
                </h3>
                <p className="text-slate-500 mb-6 max-w-md mx-auto">
                  Execute uma verificação de integridade para detectar problemas, inconsistências e potenciais fraudes no sistema.
                </p>
                <Button
                  onClick={runIntegrityCheck}
                  disabled={loading}
                  size="lg"
                  style={{ background: `linear-gradient(135deg, ${COLORS.orange} 0%, #E8850F 100%)` }}
                  className="text-white shadow-lg"
                >
                  {loading ? (
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-5 w-5 mr-2" />
                  )}
                  Iniciar Verificação
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Suspicious Activities Tab */}
        <TabsContent value="suspicious">
          <Card className="bg-white/80 backdrop-blur border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Actividades Suspeitas
                  </CardTitle>
                  <CardDescription>
                    Detecção de padrões anormais nos últimos 30 dias
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={checkSuspiciousActivities} disabled={loading}>
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Verificar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {suspicious ? (
                suspicious.suspicious_activities.length === 0 ? (
                  <div className="text-center py-12">
                    <ShieldCheck className="h-16 w-16 mx-auto text-emerald-400 mb-4" />
                    <p className="text-lg font-medium text-slate-900">Nenhuma actividade suspeita!</p>
                    <p className="text-sm text-slate-500">O sistema não detectou padrões anormais.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {suspicious.suspicious_activities.map((activity, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-4 p-4 rounded-lg border border-amber-200 bg-amber-50"
                      >
                        <div className="p-2 rounded-full bg-amber-200">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs bg-amber-100">
                              {activity.type}
                            </Badge>
                            <span className="text-sm text-slate-600">{activity.entity}</span>
                          </div>
                          <p className="font-medium text-slate-900">{activity.message}</p>
                          <p className="text-sm text-amber-700 mt-1">
                            <span className="font-medium">Recomendação:</span> {activity.recommendation}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <Search className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Clique em &quot;Verificar&quot; para analisar actividades suspeitas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history">
          <Card className="bg-white/80 backdrop-blur border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-blue-500" />
                Histórico de Verificações
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Nenhuma verificação realizada ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((check, idx) => (
                    <div 
                      key={check.id || idx}
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-4">
                        {check.status === 'passed' ? (
                          <CheckCircle className="h-6 w-6 text-emerald-500" />
                        ) : (
                          <XCircle className="h-6 w-6 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">
                            {check.status === 'passed' ? 'Sistema Íntegro' : 'Problemas Encontrados'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {new Date(check.timestamp).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' })} • {check.user_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={check.critical_count > 0 ? 'destructive' : 'secondary'}>
                          {check.critical_count} críticos
                        </Badge>
                        <Badge variant="outline">
                          {check.warning_count} avisos
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Audit Summary Tab */}
        <TabsContent value="audit">
          <Card className="bg-white/80 backdrop-blur border-slate-200">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-500" />
                    Resumo de Auditoria
                  </CardTitle>
                  <CardDescription>
                    Acções auditadas nos últimos 30 dias
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportAuditSummaryPdf}
                  disabled={exportingAuditSummary || !auditSummary}
                  data-testid="integrity-audit-summary-pdf-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportingAuditSummary ? 'A gerar PDF...' : 'PDF Premium'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {auditSummary ? (
                <div className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-center">
                      <p className="text-3xl font-bold text-blue-700">{auditSummary.breakdown.sales_audits}</p>
                      <p className="text-sm text-blue-600">Vendas Auditadas</p>
                    </div>
                    <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                      <p className="text-3xl font-bold text-emerald-700">{auditSummary.breakdown.product_audits}</p>
                      <p className="text-sm text-emerald-600">Produtos Auditados</p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200 text-center">
                      <p className="text-3xl font-bold text-purple-700">{auditSummary.breakdown.settings_audits}</p>
                      <p className="text-sm text-purple-600">Configurações</p>
                    </div>
                  </div>
                  
                  {/* Recent Critical Actions */}
                  {auditSummary.recent_critical_actions?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3">Acções Críticas Recentes</h4>
                      <div className="space-y-2">
                        {auditSummary.recent_critical_actions.map((action, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-red-50 border border-red-200">
                            <p className="font-medium text-red-900">{action.details}</p>
                            <p className="text-sm text-red-700">
                              {action.user_name} • {new Date(action.timestamp).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo' })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {selectedIssue?.message}
            </DialogTitle>
          </DialogHeader>
          
          {/* Export Buttons */}
          <div className="flex gap-2 py-2 border-b">
            <Button variant="outline" size="sm" onClick={() => exportReport('excel')}>
              <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('pdf')}>
              <FileDown className="h-4 w-4 mr-2 text-red-600" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('csv')}>
              <FileText className="h-4 w-4 mr-2 text-blue-600" />
              CSV
            </Button>
            <div className="ml-auto text-sm text-muted-foreground">
              Total: <span className="font-semibold">{detailData?.total || 0}</span> registos
            </div>
          </div>
          
          {/* Table Content */}
          <ScrollArea className="flex-1 min-h-0">
            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : detailData?.items?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectedIssue?.issueType?.startsWith('sales') ? (
                      <>
                        <TableHead>Nº Venda</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Funcionário</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Desconto</TableHead>
                        <TableHead>Estado</TableHead>
                      </>
                    ) : selectedIssue?.issueType?.startsWith('stock') ? (
                      <>
                        <TableHead>SKU</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Marca</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-right">Stock</TableHead>
                        <TableHead className="text-right">Preço</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead>ID</TableHead>
                        <TableHead>Detalhes</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailData.items.map((item, idx) => (
                    <TableRow key={item.id || idx} className="hover:bg-slate-50">
                      {selectedIssue?.issueType?.startsWith('sales') ? (
                        <>
                          <TableCell className="font-mono text-sm">{item.sale_number ? formatDisplayCode(item.sale_number, 'SAL') : 'N/A'}</TableCell>
                          <TableCell>{item.created_at?.slice(0, 10) || 'N/A'}</TableCell>
                          <TableCell>{item.customer_name || 'N/A'}</TableCell>
                          <TableCell>
                            {(item.employee_number ? `${item.employee_number} · ` : '')}{item.employee_name || (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                Não atribuído
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.total_amount || 0)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {formatCurrency(item.sale_discount || 0)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={item.status === 'completed' ? 'default' : 'secondary'}>
                              {item.status}
                            </Badge>
                          </TableCell>
                        </>
                      ) : selectedIssue?.issueType?.startsWith('stock') ? (
                        <>
                          <TableCell className="font-mono text-sm">{item.sku || 'N/A'}</TableCell>
                          <TableCell className="font-medium">{item.name || 'N/A'}</TableCell>
                          <TableCell>{item.brand || 'N/A'}</TableCell>
                          <TableCell>
                            {item.category || (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                Sem categoria
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{item.stock_quantity ?? 0}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.unit_price || 0)}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="font-mono text-sm">{item.id || 'N/A'}</TableCell>
                          <TableCell>{JSON.stringify(item).slice(0, 100)}...</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Nenhum item encontrado
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
